import { diContainer } from '@/coreContainer/container';
import {
  UnmanagedBusinessAutoRegisterConditionFieldType,
  UnmanagedBusinessAutoRegisterConditionSearchType,
  UnmanagedBusinessAutoRegisterSettingConditionItem,
  UnmanagedBusinessAutoRegisterSettingItem,
} from '@/coreDomainsModule/constants/unmanagedBusinessAutoRegister';
import { initPagingOptions } from '@/coreDomainsModule/paging';
import { i18n } from '@/i18n';
import { reactive } from 'vue';

const t = i18n.t;

/**
 * 条件値候補の管理情報
 */
type ConditionValueOptions = {
  codeOptions: string[];
  nameOptions: string[];
  valueOptions: string[];
  codeToName: Record<string, string>;
  nameToCode: Record<string, string>;
};

/**
 * 条件候補管理Composableの引数
 */
type UseUnmanagedBusinessAutoRegisterConditionOptionsOptions = {
  requiredConditionFieldTypes: UnmanagedBusinessAutoRegisterConditionFieldType[];
};

/**
 * 条件値のコード/名称ペア
 */
type ConditionCodeNamePair = {
  code: string;
  name: string;
};

/**
 * 条件候補と逆引き関連のロジックを提供する
 * @param options - 必須条件項目の定義
 */
export const useUnmanagedBusinessAutoRegisterConditionOptions = (options: UseUnmanagedBusinessAutoRegisterConditionOptionsOptions) => {
  const { requiredConditionFieldTypes } = options;

  /**
   * 条件値候補の初期値を作成する
   */
  const createDefaultConditionValueOptions = (): ConditionValueOptions => ({
    codeOptions: [],
    nameOptions: [],
    valueOptions: [],
    codeToName: {},
    nameToCode: {},
  });

  const conditionValueOptions = reactive<Record<UnmanagedBusinessAutoRegisterConditionFieldType, ConditionValueOptions>>({
    supplier: createDefaultConditionValueOptions(),
    business: createDefaultConditionValueOptions(),
    warehouse: createDefaultConditionValueOptions(),
    package: createDefaultConditionValueOptions(),
    customer: createDefaultConditionValueOptions(),
    salesDestination: createDefaultConditionValueOptions(),
  });

  /**
   * 条件項目ごとの候補管理を初期化する
   */
  const resetConditionValueOptions = (): void => {
    requiredConditionFieldTypes.forEach((targetFieldType) => {
      conditionValueOptions[targetFieldType] = createDefaultConditionValueOptions();
    });
  };

  /**
   * 条件項目の候補へ値を追加する
   * @param targetFieldType - 条件項目
   * @param searchType - 検索方法
   * @param value - 追加する値
   */
  const appendConditionOptionValue = (
    targetFieldType: UnmanagedBusinessAutoRegisterConditionFieldType,
    searchType: UnmanagedBusinessAutoRegisterConditionSearchType,
    value: string
  ): void => {
    const normalizedValue = value.trim();
    if (normalizedValue === '') {
      return;
    }
    const values = conditionValueOptions[targetFieldType];
    if (searchType === 'code') {
      if (!values.codeOptions.includes(normalizedValue)) {
        values.codeOptions = [...values.codeOptions, normalizedValue];
      }
      return;
    }
    if (searchType === 'name') {
      if (!values.nameOptions.includes(normalizedValue)) {
        values.nameOptions = [...values.nameOptions, normalizedValue];
      }
      return;
    }
    if (!values.valueOptions.includes(normalizedValue)) {
      values.valueOptions = [...values.valueOptions, normalizedValue];
    }
  };

  /**
   * コードと名称の対応を候補へ追加する
   * @param targetFieldType - 条件項目
   * @param code - コード
   * @param name - 名称
   */
  const appendConditionOptionPair = (
    targetFieldType: Exclude<UnmanagedBusinessAutoRegisterConditionFieldType, 'package'>,
    code: string,
    name: string
  ): void => {
    const normalizedCode = code.trim();
    const normalizedName = name.trim();
    if (normalizedCode === '' || normalizedName === '') {
      return;
    }

    appendConditionOptionValue(targetFieldType, 'code', normalizedCode);
    appendConditionOptionValue(targetFieldType, 'name', normalizedName);
    conditionValueOptions[targetFieldType].codeToName[normalizedCode] = normalizedName;
    conditionValueOptions[targetFieldType].nameToCode[normalizedName] = normalizedCode;
  };

  /**
   * マスタ取得結果をコード/名称ペアへ変換する
   * @param codes - コード配列
   * @param names - 名称配列
   */
  const toConditionCodeNamePairs = (codes: string[] = [], names: string[] = []): ConditionCodeNamePair[] => {
    const maxPairCount = Math.min(codes.length, names.length);
    const pairs: ConditionCodeNamePair[] = [];
    for (let index = 0; index < maxPairCount; index += 1) {
      pairs.push({
        code: codes[index] ?? '',
        name: names[index] ?? '',
      });
    }
    return pairs;
  };

  /**
   * マスタから条件候補のコード/名称ペアを取得する
   * @param targetFieldType - 条件項目
   * @param pagingOptions - ページング情報
   */
  const loadConditionCodeNamePairsFromMaster = async (
    targetFieldType: Exclude<UnmanagedBusinessAutoRegisterConditionFieldType, 'package'>,
    pagingOptions: ReturnType<typeof initPagingOptions>
  ): Promise<void> => {
    const masterLoaderMap = {
      supplier: {
        load: diContainer.filterInfoRepository.getSupplierNameListFromMaster.bind(diContainer.filterInfoRepository),
        codeKey: 'supplierNumbers',
        nameKey: 'supplierNames',
      },
      business: {
        load: diContainer.filterInfoRepository.getBusinessNameListFromMaster.bind(diContainer.filterInfoRepository),
        codeKey: 'businessNumbers',
        nameKey: 'businessNames',
      },
      warehouse: {
        load: diContainer.filterInfoRepository.getWarehouseListFromMaster.bind(diContainer.filterInfoRepository),
        codeKey: 'warehouseNumbers',
        nameKey: 'warehouses',
      },
      customer: {
        load: diContainer.filterInfoRepository.getCustomerNameListFromMaster.bind(diContainer.filterInfoRepository),
        codeKey: 'customerNumbers',
        nameKey: 'customerNames',
      },
      salesDestination: {
        load: diContainer.filterInfoRepository.getSalesDestinationNameListFromMaster.bind(diContainer.filterInfoRepository),
        codeKey: 'salesDestinationNumbers',
        nameKey: 'salesDestinationNames',
      },
    } as const;

    const loader = masterLoaderMap[targetFieldType];
    const result = await loader.load(pagingOptions);
    if (result.status.type !== 'success') {
      return;
    }

    const codeValues = result.status.data.data[loader.codeKey] ?? [];
    const nameValues = result.status.data.data[loader.nameKey] ?? [];
    toConditionCodeNamePairs(codeValues, nameValues).forEach(({ code, name }) => {
      appendConditionOptionPair(targetFieldType, code, name);
    });
  };

  /**
   * マスタから容量候補を取得する
   * @param pagingOptions - ページング情報
   */
  const loadPackageValuesFromApi = async (pagingOptions: ReturnType<typeof initPagingOptions>): Promise<void> => {
    const result = await diContainer.getFilterInfoUseCase.Execute('PACKAGE_AMOUNT', '', 'inventory', undefined, undefined, pagingOptions);
    if (result.status.type !== 'success') {
      return;
    }

    result.status.data.data.forEach((value) => {
      appendConditionOptionValue('package', 'value', String(value));
    });
  };

  /**
   * 条件候補をAPIから取得して反映する
   */
  const loadConditionValueOptionsFromApi = async (): Promise<void> => {
    const pagingOptions = initPagingOptions(2000);

    await Promise.all(
      requiredConditionFieldTypes.map(async (targetFieldType) => {
        if (targetFieldType === 'package') {
          await loadPackageValuesFromApi(pagingOptions);
          return;
        }
        await loadConditionCodeNamePairsFromMaster(targetFieldType, pagingOptions);
      })
    );
  };

  /**
   * 条件候補へ保存済み設定の値を反映する
   * @param settings - 設定一覧
   */
  const appendConditionValueOptionsFromSettings = (settings: UnmanagedBusinessAutoRegisterSettingItem[]): void => {
    settings.forEach((setting) => {
      setting.conditions.forEach((conditionItem) => {
        if (!requiredConditionFieldTypes.includes(conditionItem.targetFieldType)) {
          return;
        }
        if (conditionItem.targetFieldType !== 'package' && conditionItem.searchType === 'code') {
          const normalizedTargetName = conditionItem.targetName?.trim();
          if (normalizedTargetName != null && normalizedTargetName !== '') {
            appendConditionOptionPair(conditionItem.targetFieldType, conditionItem.targetValue, normalizedTargetName);
            return;
          }
        }
        appendConditionOptionValue(conditionItem.targetFieldType, conditionItem.searchType, conditionItem.targetValue);
      });
    });
  };

  /**
   * 条件項目+検索方法に応じた候補一覧を返す
   * @param conditionItem - 条件
   */
  const getConditionValueOptions = (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem): string[] => {
    const values = conditionValueOptions[conditionItem.targetFieldType];
    if (conditionItem.searchType === 'code') {
      return values.codeOptions;
    }
    if (conditionItem.searchType === 'name') {
      return values.nameOptions;
    }
    return values.valueOptions;
  };

  /**
   * 条件項目の逆引き値を取得する
   * @param conditionItem - 条件
   */
  const getConditionCounterpartValue = (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem): string => {
    if (conditionItem.targetFieldType === 'package') {
      return '-';
    }

    const values = conditionValueOptions[conditionItem.targetFieldType];
    if (conditionItem.searchType === 'code') {
      return values.codeToName[conditionItem.targetValue] ?? '-';
    }
    if (conditionItem.searchType === 'name') {
      return values.nameToCode[conditionItem.targetValue] ?? '-';
    }
    return '-';
  };

  /**
   * 条件値からコード/名称ペアを取得する
   * @param conditionItem - 条件
   */
  const getConditionCodeNamePair = (
    conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem
  ): ConditionCodeNamePair | undefined => {
    if (conditionItem.targetFieldType === 'package') {
      return undefined;
    }

    const targetValue = conditionItem.targetValue.trim();
    if (targetValue === '') {
      return undefined;
    }

    const values = conditionValueOptions[conditionItem.targetFieldType];
    if (conditionItem.searchType === 'code') {
      const name = values.codeToName[targetValue];
      if (name == null || name === '') {
        return undefined;
      }
      return {
        code: targetValue,
        name,
      };
    }

    if (conditionItem.searchType === 'name') {
      const code = values.nameToCode[targetValue];
      if (code == null || code === '') {
        return undefined;
      }
      return {
        code,
        name: targetValue,
      };
    }

    return undefined;
  };

  /**
   * 条件項目の逆引きラベルを取得する
   * @param conditionItem - 条件
   */
  const getConditionCounterpartLabel = (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem): string => {
    if (conditionItem.targetFieldType === 'package') {
      return t('逆引き') as string;
    }
    return conditionItem.searchType === 'code' ? (t('名称') as string) : (t('コード') as string);
  };

  /**
   * 条件項目の逆引き表示文字列を取得する
   * @param conditionItem - 条件
   */
  const getConditionCounterpartText = (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem): string => {
    if (conditionItem.targetFieldType === 'package') {
      return '';
    }
    return `${getConditionCounterpartLabel(conditionItem)}：${getConditionCounterpartValue(conditionItem)}`;
  };

  /**
   * 条件値変更時に逆引き候補を補完する
   * @param conditionItem - 更新対象の条件
   */
  const syncConditionCounterpartOption = async (conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem): Promise<void> => {
    const targetValue = conditionItem.targetValue.trim();
    if (targetValue === '' || conditionItem.targetFieldType === 'package') {
      return;
    }
    if (getConditionCounterpartValue(conditionItem) !== '-') {
      return;
    }

    const lookupUseCaseMap: Record<
      Exclude<UnmanagedBusinessAutoRegisterConditionFieldType, 'package'>,
      {
        code: {
          execute: (key: string) => Promise<{ status: { type: string; data?: string } }>;
        };
        name: {
          execute: (key: string) => Promise<{ status: { type: string; data?: string } }>;
        };
      }
    > = {
      supplier: {
        code: diContainer.getSupplierNameUseCase,
        name: diContainer.getSupplierNumberUseCase,
      },
      business: {
        code: diContainer.getBusinessNameUseCase,
        name: diContainer.getBusinessNumberUseCase,
      },
      warehouse: {
        code: diContainer.getWarehouseNameUseCase,
        name: diContainer.getWarehouseNumberUseCase,
      },
      customer: {
        code: diContainer.getCustomerNameUseCase,
        name: diContainer.getCustomerNumberUseCase,
      },
      salesDestination: {
        code: diContainer.getSalesDestinationNameUseCase,
        name: diContainer.getSalesDestinationNumberUseCase,
      },
    };

    const targetUseCase = lookupUseCaseMap[conditionItem.targetFieldType][conditionItem.searchType];
    const result = await targetUseCase.execute(targetValue);
    if (result.status.type !== 'success' || result.status.data == null) {
      return;
    }

    if (conditionItem.searchType === 'code') {
      appendConditionOptionPair(conditionItem.targetFieldType, targetValue, result.status.data);
      return;
    }
    appendConditionOptionPair(conditionItem.targetFieldType, result.status.data, targetValue);
  };

  /**
   * 条件値をコード/名称ペアへ正規化する
   * @param conditionItem - 正規化対象の条件
   */
  const resolveConditionCodeNamePair = async (
    conditionItem: UnmanagedBusinessAutoRegisterSettingConditionItem
  ): Promise<ConditionCodeNamePair | undefined> => {
    if (conditionItem.targetFieldType === 'package') {
      return undefined;
    }

    const existingPair = getConditionCodeNamePair(conditionItem);
    if (existingPair != null) {
      return existingPair;
    }

    await syncConditionCounterpartOption(conditionItem);
    return getConditionCodeNamePair(conditionItem);
  };

  return {
    resetConditionValueOptions,
    appendConditionOptionValue,
    appendConditionOptionPair,
    loadConditionValueOptionsFromApi,
    appendConditionValueOptionsFromSettings,
    getConditionValueOptions,
    getConditionCounterpartText,
    getConditionCodeNamePair,
    resolveConditionCodeNamePair,
    syncConditionCounterpartOption,
  };
};
